<template>
  <div class="form-step">
    <v-row>
      <v-col cols="2">
        <v-label class="mb-2">Target Value:</v-label>
        <v-text-field
          label="Enter a real value"
          variant="solo"
          type="number"
          step="0.01"
          v-model="form.target_value"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-checkbox
          label="Use relative margins?"
          v-model="form.relative_margins"
          true-value="true"
          false-value="false"
        >
        </v-checkbox>
      </v-col>
      <v-row>
        <v-col cols="12" class="ml-3">
          <div class="mb-3">
            <label class="label"><b class="mr-2">Provide the margins</b>
              <v-tooltip text="The values of the margins must be in ascending or descending order."
              >
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    size="x-small"
                    icon
                    variant="outlined"
                    color="#0d47a1"
                  >
                    <v-icon color="black" icon="fas fa-info" />
                  </v-btn>
                </template>
              </v-tooltip>
            </label>
          </div>
        </v-col>
        <v-col
          cols="2"
          v-for="index in [0, 1, 2, 3]"
          class="mb-2 ml-3"
          :key="`m${index}`"
        >
          <v-card elevation="4" class="rounded-xl" max-width="270">
            <v-text-field
              :label="getLabel(index)"
              type="number"
              v-model="form[`m${index}`]"
              step="0.001"
              min="-10000.00"
              max="10000.00"
              :style="`background: ${getColor(index)}`"
            ></v-text-field>
          </v-card>
        </v-col>

        <v-col cols="2" class="mb-3">
          <v-card elevation="4" class="rounded-xl" max-width="270">
            <v-form
              :label="getLabel(4)"
              :style="`background: ${getColor(4)};`"
              class="likert-input"
            >
              <v-text-field type="text" :value="m4" readonly></v-text-field>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
      <v-col cols="12" class="pt-8">
        <div v-if="monotonous === 'error'" style="color: red" class="label">
          Wrong margins
        </div>

        <MarginsDistributionPlot
          v-if="monotonous === 'asc' || monotonous === 'desc'"
          :values="[form.m0, form.m1, form.m2, form.m3]"
          :isPercentage="percentageMargins"
        ></MarginsDistributionPlot>
        <span v-else class="label"
          >The distribution plot will be generated after you have given all
          margin inputs.</span
        >
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import MarginsDistributionPlot from "../../../Shared/MarginsDistributionPlot.vue";
import Utils from "../../../Mixins/Utils";
import { computed } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
});

const form = props.modelValue;

const m4 = computed(() => {
  if (monotonous.value === "asc") return `>= ${form.m3}`;
  else if (monotonous.value === "desc") return `<= ${form.m3}`;
  else return null;
});

const monotonous = computed(() => {
  const psi = form;
  return Utils.methods.isMonotonic([psi.m0, psi.m1, psi.m2, psi.m3]);
});

const percentageMargins = computed(() => {
  return form.relative_margins;
});

function getLabel(index) {
  if (index === 0) return "Failed";
  else if (index === 1) return "Underachieved";
  else if (index === 2) return "Achieved";
  else if (index === 3) return "Overachieved";
  else return "Excelled";
}

function getColor(index) {
  switch (index) {
    case 0:
      return '#ff7171';
    case 1:
      return '#ffb673';
    case 2:
      return '#e2ff73';
    case 3:
      return '#93ff5eb2';
    default:
      return '#30cf00';
  }
}
</script>

<style scoped>
.label {
  font-size: medium;
}
</style>
